import React from 'react';

/**
 * CollabPersonaModal — asks merged host+creator accounts which side of a
 * collab they're on before we create or accept it, instead of guessing from
 * the session's active persona (which put people on the wrong side of the
 * collab tracker).
 *
 * Rendered only when the user actually has both roles; single-persona users
 * never see it.
 */

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  background: 'rgba(15, 8, 75, 0.45)',
};

const cardStyle = {
  width: '100%',
  maxWidth: '420px',
  background: '#ffffff',
  borderRadius: '16px',
  padding: '28px 24px 24px',
  boxShadow: '0 16px 48px rgba(15, 8, 75, 0.25)',
  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
};

const optionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '14px 16px',
  borderRadius: '12px',
  border: '1.5px solid #e6e8ec',
  background: '#ffffff',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.15s ease',
};

const OptionIcon = ({ children }) => (
  <span
    aria-hidden
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '38px',
      height: '38px',
      borderRadius: '50%',
      background: 'rgba(15, 8, 75, 0.06)',
      color: '#0f084b',
      flexShrink: 0,
    }}
  >
    {children}
  </span>
);

const CollabPersonaModal = ({ isOpen, busy = false, onSelect, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={busy ? undefined : onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 6px', fontSize: '19px', fontWeight: 700, color: '#0f084b' }}>
          Which account is this collab for?
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: '14px', lineHeight: 1.5, color: '#6b7280' }}>
          We noticed you&rsquo;re both a host and a creator on Postaway. Choose which one
          you&rsquo;re collabing as — you can change it later from the collab.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            type="button"
            disabled={busy}
            style={{ ...optionStyle, opacity: busy ? 0.6 : 1 }}
            onClick={() => onSelect('host')}
          >
            <OptionIcon>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </OptionIcon>
            <span>
              <strong style={{ display: 'block', fontSize: '15px', color: '#0f084b' }}>As a Host</strong>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Collab on behalf of my property</span>
            </span>
          </button>

          <button
            type="button"
            disabled={busy}
            style={{ ...optionStyle, opacity: busy ? 0.6 : 1 }}
            onClick={() => onSelect('creator')}
          >
            <OptionIcon>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
            </OptionIcon>
            <span>
              <strong style={{ display: 'block', fontSize: '15px', color: '#0f084b' }}>As a Creator</strong>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Collab with my creator account</span>
            </span>
          </button>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          style={{
            marginTop: '16px',
            width: '100%',
            padding: '10px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            fontSize: '13px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CollabPersonaModal;
